import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Camera, Upload, Save, Phone, User, MapPin, Calendar, AtSign } from 'lucide-react';
import { authActions } from '../../../redux/actions';
import WrapUpP2p from '../p2p/layouts/WrapUpP2p';
import { guessAdminRoute, notify } from '../../../utility/Helper1';
import { Avatar } from '@mui/material';
import { userSelect } from '../../../redux/selectors';

const Settingsprofile = () => {
    const userData = useSelector(userSelect);
    const dispatch = useDispatch();

    // State management
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [phoneUpdate, setPhoneUpdate] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState(userData?.phone || '');
    const [accountUpdate, setAccountUpdate] = useState(false);
    const [profileData, setProfileData] = useState({
        firstname: userData?.firstname || '',
        middlename: userData?.middlename || '',
        surname: userData?.surname || '',
        DOB: userData?.DOB || '',
        sex: userData?.sex || '',
        address: userData?.address || '',
        city: userData?.city || '',
        postalCode: userData?.postalCode || '',
    });


    // Handler functions
    const uploadAvatar = async (file) => {

        if (uploadingAvatar) { return; }

        setUploadingAvatar(true)

        dispatch(authActions.onLogin("account/change-avatar", { avatar: file }, response => {
            setUploadingAvatar(false)
        }),
        )

    }

    const updateProfileInfo = async () => {

        if (accountUpdate) { return; }

        setAccountUpdate(true)

        dispatch(authActions.onLogin("account/update/info", profileData, response => {
            setAccountUpdate(false)
            notify(response?.msg, response?.code)
        }),
        )


    };

    const updatePhoneNumber = async (file) => {

        if (phoneUpdate) { return; }

        setPhoneUpdate(true)

        dispatch(authActions.onLogin("account/update/phone", { phone: phoneNumber }, response => {
            setPhoneUpdate(false)
            notify(response?.msg, response?.code)
        }),
        )

    }


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (<WrapUpP2p>
        <div className="max-w-6xl mx-auto p-6 bg-gradient-to-b from-green-50 to-white rounded-xl shadow-lg">
            <h1 className="text-3xl font-bold text-green-800 mb-8 border-b border-green-200 pb-4">Profile Settings</h1>

            <div className="grid md:grid-cols-3 gap-8">
                {/* Left Column - Profile Picture */}
                <div className="md:col-span-1">
                    <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
                        <h2 className="text-xl font-semibold text-green-800 mb-6 flex items-center">
                            <User className="mr-2 text-green-600" size={20} />
                            Profile Picture
                        </h2>

                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative group">
                                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-green-500 shadow-md">

                                    <Avatar
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                        }}
                                        src={`${guessAdminRoute()}/${userData?.image}`}
                                    />

                                </div>

                                {uploadingAvatar && (
                                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                                        <div className="animate-spin text-white">
                                            <Camera size={24} />
                                        </div>
                                    </div>
                                )}

                                <div className="absolute bottom-0 right-0">
                                    <label
                                        htmlFor="avatar-upload"
                                        className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-full hover:bg-green-700 transition cursor-pointer shadow-lg"
                                    >
                                        <Camera size={18} />
                                    </label>
                                    <input
                                        type="file"
                                        id="avatar-upload"
                                        className="hidden"
                                        onChange={(e) => uploadAvatar(e.target.files[0])}
                                    />
                                </div>
                            </div>

                            <div className="text-center">
                                <h3 className="font-medium text-green-900 text-lg">{userData?.username}</h3>
                                <p className="text-sm text-green-600">{userData?.email}</p>
                            </div>

                            <p className="text-xs text-green-600 bg-green-50 p-2 rounded-md w-full text-center">
                                Max file size: 4MB
                            </p>

                            {/* <label
                                htmlFor="avatar-upload-btn"
                                className="flex items-center justify-center w-full py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition cursor-pointer shadow-sm"
                            >
                                <Upload className="mr-2" size={18} />
                                Upload New Avatar
                            </label> */}
                            <input
                                type="file"
                                id="avatar-upload-btn"
                                className="hidden"
                                onChange={(e) => uploadAvatar(e.target.files[0])}
                            />
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="bg-white p-6 rounded-xl shadow-md border border-green-100 mt-6">
                        <h2 className="text-xl font-semibold text-green-800 mb-6 flex items-center">
                            <Phone className="mr-2 text-green-600" size={20} />
                            Contact Information
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">Phone Number</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-3 text-sm text-green-900 bg-green-100 border border-r-0 border-green-300 rounded-l-md">
                                        {userData?.countryPhoneCode || '+1'}
                                    </span>
                                    <input
                                        type="text"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        className="flex-1 rounded-r-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        placeholder="Enter phone number"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={updatePhoneNumber}
                                disabled={phoneUpdate}
                                className="w-full py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {phoneUpdate ? (
                                    <>
                                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                        Updating...
                                    </>
                                ) : (
                                    <>Update Phone Number</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Column - Personal Information */}
                <div className="md:col-span-2">
                    <div className="bg-white p-6 rounded-xl shadow-md border border-green-100">
                        <h2 className="text-xl font-semibold text-green-800 mb-6 flex items-center">
                            <AtSign className="mr-2 text-green-600" size={20} />
                            Personal Information
                        </h2>

                        <div className="grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="firstname"
                                    value={profileData.firstname}
                                    onChange={handleInputChange}
                                    className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    placeholder="Enter first name"
                                />
                            </div>

                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">Middle Name</label>
                                <input
                                    type="text"
                                    name="middlename"
                                    value={profileData.middlename}
                                    onChange={handleInputChange}
                                    className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    placeholder="Enter middle name"
                                />
                            </div>

                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="surname"
                                    value={profileData.surname}
                                    onChange={handleInputChange}
                                    className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    placeholder="Enter last name"
                                />
                            </div>

                            {1 > 2 && <>
                                <div>
                                    <label className="block text-green-700 text-sm font-medium mb-2 flex items-center">
                                        <Calendar className="mr-1 text-green-600" size={16} />
                                        Date of Birth
                                    </label>
                                    <input
                                        type="date"
                                        name="DOB"
                                        value={profileData.DOB}
                                        onChange={handleInputChange}
                                        className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    />
                                </div>

                                <div>
                                    <label className="block text-green-700 text-sm font-medium mb-2">Gender</label>
                                    <select
                                        name="sex"
                                        value={profileData.sex}
                                        onChange={handleInputChange}
                                        className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="0">Male</option>
                                        <option value="1">Female</option>
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-green-700 text-sm font-medium mb-2 flex items-center">
                                        <MapPin className="mr-1 text-green-600" size={16} />
                                        Address
                                    </label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={profileData.address}
                                        onChange={handleInputChange}
                                        className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        placeholder="Enter address"
                                    />
                                </div>

                                <div>
                                    <label className="block text-green-700 text-sm font-medium mb-2">City</label>
                                    <input
                                        type="text"
                                        name="city"
                                        value={profileData.city}
                                        onChange={handleInputChange}
                                        className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        placeholder="Enter city"
                                    />
                                </div>

                                <div>
                                    <label className="block text-green-700 text-sm font-medium mb-2">Postal Code</label>
                                    <input
                                        type="text"
                                        name="postalCode"
                                        value={profileData.postalCode}
                                        onChange={handleInputChange}
                                        className="w-full rounded-md border-green-300 focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                                        placeholder="Enter postal code"
                                    />
                                </div>
                            </>}

                        </div>

                        {/* <div className="mt-8 grid md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">Email</label>
                                <input
                                    type="email"
                                    value={userData?.email || ''}
                                    disabled
                                    className="w-full rounded-md bg-green-50 border-green-200 cursor-not-allowed text-green-700"
                                />
                                <p className="text-xs text-green-600 mt-1">Contact support to change email</p>
                            </div>

                            <div>
                                <label className="block text-green-700 text-sm font-medium mb-2">Username</label>
                                <input
                                    type="text"
                                    value={userData?.username || ''}
                                    disabled
                                    className="w-full rounded-md bg-green-50 border-green-200 cursor-not-allowed text-green-700"
                                />
                                <p className="text-xs text-green-600 mt-1">Username cannot be changed</p>
                            </div>
                        </div> */}

                        <button
                            onClick={updateProfileInfo}
                            disabled={accountUpdate}
                            className="w-full mt-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                        >
                            {accountUpdate ? (
                                <>
                                    <div className="animate-spin mr-2 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                    Updating Profile...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2" size={20} />
                                    Save Profile
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </WrapUpP2p>
    );
};

export default Settingsprofile;
